/**
 * Authentication Utilities
 *
 * Centralized utilities for JWT decoding and role-based access control.
 * Frontend role checks are cosmetic only - backend must enforce authorization.
 */

import { jwtDecode } from 'jwt-decode';

/**
 * Cognito ID Token JWT payload structure
 */
export interface CognitoIdTokenPayload {
  sub: string;
  email: string;
  email_verified: boolean;
  phone_number?: string;
  phone_number_verified?: boolean;
  name?: string;
  'cognito:username': string;
  'cognito:groups'?: string[];
  aud: string;
  token_use: 'id';
  auth_time: number;
  iss: string;
  exp: number;
  iat: number;
}

/**
 * User roles/groups used in the application
 */
export const USER_GROUPS = {
  ADMINS: 'Admins',
  ACCOUNT_MANAGERS: 'AccountManagers',
  AUTO_LOGIN_USERS: 'AutoLoginUsers',
  SUPER_ADMINS: 'SuperAdmins',
} as const;

export type UserGroup = typeof USER_GROUPS[keyof typeof USER_GROUPS];

/**
 * Decode an ID token and return the payload
 * @param idToken - The JWT ID token from Cognito
 * @returns Decoded token payload or null if invalid
 */
export const decodeIdToken = (idToken: string): CognitoIdTokenPayload | null => {
  try {
    return jwtDecode<CognitoIdTokenPayload>(idToken);
  } catch (error) {
    console.error('Failed to decode ID token:', error);
    return null;
  }
};

/**
 * Extract user groups from an ID token
 * @param idToken - The JWT ID token from Cognito
 * @returns Array of group names the user belongs to
 */
export const getUserGroups = (idToken: string): string[] => {
  const decoded = decodeIdToken(idToken);
  return decoded?.['cognito:groups'] || [];
};

/**
 * Check if user belongs to the Admins group
 * @param idToken - The JWT ID token from Cognito
 * @returns true if user is an admin
 */
export const isAdmin = (idToken: string): boolean => {
  return getUserGroups(idToken).includes(USER_GROUPS.ADMINS);
};

/**
 * Check if user belongs to a specific group
 * @param idToken - The JWT ID token from Cognito
 * @param group - The group name to check
 * @returns true if user belongs to the group
 */
export const hasGroup = (idToken: string, group: string): boolean => {
  return getUserGroups(idToken).includes(group);
};

/**
 * Check if user belongs to any of the specified groups
 * @param idToken - The JWT ID token from Cognito
 * @param groups - Array of group names to check
 * @returns true if user belongs to at least one group
 */
export const hasAnyGroup = (idToken: string, groups: string[]): boolean => {
  const userGroups = getUserGroups(idToken);
  return groups.some(group => userGroups.includes(group));
};

/**
 * Phase 2 predicate: can the user manage other Zebu users' accounts?
 * True for AccountManagers and (implicitly) Admins. Pure helper — operates
 * on a pre-decoded `groups` array so AuthContext can call it from a memo
 * without re-decoding the token on every render.
 */
export const isAccountManagerGroup = (groups: string[] | undefined | null): boolean => {
  if (!groups) return false;
  return groups.includes(USER_GROUPS.ADMINS) || groups.includes(USER_GROUPS.ACCOUNT_MANAGERS);
};

/**
 * Phase 1 predicate: can the user enable self-service auto-login on their
 * own broker row? True for AutoLoginUsers + AccountManagers + Admins
 * (matches the backend gate in lambda_functions/broker/_admin_gate.py).
 */
export const isManagedAccountUserGroup = (groups: string[] | undefined | null): boolean => {
  if (!groups) return false;
  return (
    groups.includes(USER_GROUPS.ADMINS) ||
    groups.includes(USER_GROUPS.ACCOUNT_MANAGERS) ||
    groups.includes(USER_GROUPS.AUTO_LOGIN_USERS)
  );
};

/**
 * marketplace-publisher-groups predicate: SuperAdmin role check.
 * SuperAdmins own publisher-group CRUD, membership CRUD, and managed-user
 * pool assignment.
 */
export const isSuperAdminGroup = (groups: string[] | undefined | null): boolean => {
  if (!groups) return false;
  return groups.includes(USER_GROUPS.SUPER_ADMINS);
};

/**
 * Check if the token is expired
 * @param idToken - The JWT ID token from Cognito
 * @returns true if token is expired
 */
export const isTokenExpired = (idToken: string): boolean => {
  const decoded = decodeIdToken(idToken);
  if (!decoded) return true;

  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
};